import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Clock, AlertTriangle, Hourglass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useStartShift, useOpenShift } from "../hooks";
import { useToast } from "@/hooks/use-toast";
import { getCurrentPosition, type Coords } from "@/shared/utils/location";
import { LocationCheckCard } from "../components/LocationCheckCard";
import { isWithinOneMinuteOfNow } from "../utils/manualTimeUtils";
import { NativeTimeInput } from "@/components/ui/native-time-input";

export function ConfirmStartShiftPage() {
  const navigate = useNavigate();
  const location = useLocation();
  useOpenShift();
  const startShiftMutation = useStartShift();
  const { toast } = useToast();
  const [notes, setNotes] = useState("");
  const [manualArrivalTime, setManualArrivalTime] = useState<string>("");
  const [manualArrivalExpanded, setManualArrivalExpanded] = useState(false);
  const manualArrivalSectionRef = useRef<HTMLElement | null>(null);
  const navState = location.state as { location?: Coords | null } | null;
  const hasLocationFromNav = navState != null;
  const [locationCoords, setLocationCoords] = useState<Coords | null>(() =>
    hasLocationFromNav ? navState.location ?? null : null
  );
  const [locationLoading, setLocationLoading] = useState(!hasLocationFromNav);

  // If we didn't get location from navigation, fetch on mount.
  useEffect(() => {
    if (!hasLocationFromNav) {
      getCurrentPosition().then((coords) => {
        setLocationCoords(coords ?? null);
        setLocationLoading(false);
      });
    }
  }, [hasLocationFromNav]);

  // Get current time for logged start
  const loggedStartTime = useMemo(() => {
    const now = new Date();
    return now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  // Convert time string (HH:MM) to ISO 8601 datetime string
  const formatManualArrivalTime = (timeString: string): string | undefined => {
    if (!timeString) return undefined;
    
    const [hours, minutes] = timeString.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    
    return date.toISOString();
  };

  const quickNoteOptions = [
    "Traffic Delay",
    "Public Transport",
    "Personal",
  ];

  const handleQuickNoteClick = (option: string) => {
    if (notes.trim() === "") {
      setNotes(option);
    } else {
      setNotes((prev) => `${prev}${prev.endsWith(".") ? " " : ". "}${option}`);
    }
  };

  const handleConfirm = async () => {
    if (startShiftMutation.isPending) return;

    try {
      const coords = locationCoords ?? (await getCurrentPosition());
      const manualArrivalTimeISO = formatManualArrivalTime(manualArrivalTime);
      const omitManualTime =
        manualArrivalTimeISO != null && isWithinOneMinuteOfNow(manualArrivalTimeISO);
      await startShiftMutation.mutateAsync({
        notes: notes.trim() || undefined,
        manualArrivalTime: omitManualTime ? undefined : manualArrivalTimeISO,
        coords,
      });
      toast({
        title: "Shift started",
        description: "Your shift has been started successfully.",
      });
      navigate("/shifts");
    } catch (error) {
      toast({
        title: "Failed to start shift",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    navigate("/tasks");
  };

  const openManualArrivalFromFooter = () => {
    setManualArrivalExpanded(true);
    window.setTimeout(() => {
      const el = manualArrivalSectionRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex-1 overflow-y-auto">
        {/* Logged Start Section */}
        <section className="bg-white rounded-b-3xl border-b border-gray-200 p-4 relative overflow-hidden">
          <div className="relative z-10 w-1/2 bg-gray-50 p-2 rounded-lg border-gray-200 border">
            <p className="text-sm text-gray-500 mb-1">Logged Start</p>
            <p className="text-xl font-bold text-gray-900">{loggedStartTime}</p>
          </div>
          <div className="absolute right-0 top-0 opacity-5">
            <Clock className="h-24 w-24 text-gray-900" />
          </div>
        </section>

        {/* Manual Arrival Time — opened via footer link; card stays in original scroll position */}
        {manualArrivalExpanded && (
          <section
            ref={manualArrivalSectionRef}
            className="bg-white rounded-xl m-4 p-4 border-gray-200 border"
          >
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Hourglass className="h-5 w-5 shrink-0 text-blue-600" />
                <h2 className="text-lg mt-2 text-gray-900">Manual Arrival Time</h2>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                  Optional
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setManualArrivalExpanded(false);
                    setManualArrivalTime("");
                  }}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  Hide
                </button>
              </div>
            </div>
            <NativeTimeInput
              value={manualArrivalTime}
              onChange={setManualArrivalTime}
              aria-label="Manual arrival time"
              data-testid="manual-arrival-time"
            />
            <div className="mt-3 p-3 bg-yellow-50 border border-orange-200 rounded-lg">
              <div className="flex gap-3 items-center">
                <div className="flex-shrink-0 flex items-center">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-orange-900 text-sm mb-1 mt-1">
                    Approval Required
                  </h4>
                  <p className="text-sm text-orange-800">
                    If you are setting a manual arrival time your manager will need to
                    approve it.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        <div className={manualArrivalExpanded ? undefined : "mt-4"}>
          <LocationCheckCard
            coords={locationCoords}
            loading={locationLoading}
            showMap={false}
            geofencingNoticeVariant="start"
          />
        </div>

        {/* Shift Notes Section */}
        <section className="bg-white rounded-xl m-4 p-4 border-gray-200 border">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Shift Notes</h2>
          <Textarea
            placeholder="Add reason for late arrival or other notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 resize-none mb-3"
          />
          <div className="flex flex-wrap gap-2">
            {quickNoteOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleQuickNoteClick(option)}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {option}
              </button>
            ))}
          </div>
        </section>
      </div>

      {/* Action Buttons */}
      <section className="px-4 pb-6 space-y-3 bg-white border-t border-gray-200 pt-4">
        <Button
          onClick={handleConfirm}
          disabled={startShiftMutation.isPending}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg h-14"
        >
          <span>Confirm Start Shift</span>
          <ArrowRight className="h-5 w-5" />
        </Button>
        <button
          onClick={handleCancel}
          disabled={startShiftMutation.isPending}
          className="w-full text-center text-gray-700 font-medium py-2 hover:text-gray-900 transition-colors"
        >
          Cancel
        </button>
        {!manualArrivalExpanded && (
          <button
            type="button"
            onClick={openManualArrivalFromFooter}
            className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-800 underline-offset-4 hover:underline py-1 pt-2 bg-transparent border-0 cursor-pointer"
          >
            Add manual arrival time
          </button>
        )}
      </section>
    </div>
  );
}

